import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { fetchChangeOrderLog, downloadChangeOrderLogPdf, type ChangeOrderLogEntry } from '../../api/projects'
import { formatUsd } from './pulseTotals.js'
import './pulse.css'

export type CoLogTreeData = {
  project: { id: number; name?: string; number?: string }
  original_contract: number
  entries: ChangeOrderLogEntry[]
  current_contract: number
}

type Props = {
  projectId: number
  onOpenCo?: (coId: number) => void
  compact?: boolean
}

function displaySignedAmount(amount: number, amountDisplay: string): string {
  if (amount < 0) return amountDisplay.replace(/^-/, '−')
  return amountDisplay
}

function contractPctDelta(original: number, current: number): string {
  if (!(original > 0)) return ''
  const pct = ((current - original) / original) * 100
  const sign = pct >= 0 ? '+' : ''
  return `${sign}${pct.toFixed(1)}% vs original`
}

function coStatusBadge(entry: ChangeOrderLogEntry) {
  const partial = entry.billing_status === 'partly_billed'
  return (
    <span className={`co-log-badge${partial ? ' co-log-badge--partial' : ''}`}>
      {entry.billing_label}
    </span>
  )
}

function coNumberLabel(n: number): string {
  return String(n).padStart(2, '0')
}

export default function CoLogTree({ projectId, onOpenCo, compact }: Props) {
  const [printing, setPrinting] = useState(false)
  const q = useQuery({
    queryKey: ['change-order-log', projectId],
    queryFn: () => fetchChangeOrderLog(projectId),
    enabled: Number.isFinite(projectId),
  })

  async function onPrint() {
    if (printing) return
    setPrinting(true)
    try {
      await downloadChangeOrderLogPdf(projectId)
    } finally {
      setPrinting(false)
    }
  }

  if (q.isLoading) return <p className="pulse-row-meta" data-testid="co-log-loading">Loading change order log…</p>
  if (q.isError) {
    return <p className="pulse-row-meta" data-testid="co-log-error">Could not load change order log.</p>
  }
  const tree = q.data
  if (!tree) return null

  const coCount = tree.entries.length
  const coDelta = tree.current_contract - tree.original_contract
  const pctLine = contractPctDelta(tree.original_contract, tree.current_contract)

  return (
    <section
      id="co-log-tree"
      className={`co-log-tree${compact ? ' co-log-tree--compact' : ''}`}
      data-testid="co-log-tree"
      aria-label="Change Order Log"
    >
      <div className="co-log-tree__head">
        <div>
          <h3 className="co-log-tree__title">Change Order Log</h3>
          <p className="co-log-tree__sub">From the original agreement to the current contract.</p>
        </div>
        <button
          type="button"
          className="btn b-ghost btn-sm"
          data-testid="co-log-print"
          disabled={printing}
          onClick={() => void onPrint()}
        >
          {printing ? '…' : 'Print'}
        </button>
      </div>
      <div className="co-log-tree__tablewrap">
        <table className="co-log-tree__table">
          <thead>
            <tr>
              <th>CO #</th>
              <th>Title / attachment</th>
              <th className="co-log-tree__num">Amount</th>
              <th className="co-log-tree__num">Running total</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            <tr className="co-log-tree__summary" data-testid="co-log-original">
              <td>—</td>
              <td>Original contract</td>
              <td className="co-log-tree__num">—</td>
              <td className="co-log-tree__num">{formatUsd(tree.original_contract)}</td>
              <td><span className="co-log-badge">Original agreement</span></td>
            </tr>
            {tree.entries.map((e) => {
              const file = e.attachments[0]?.original_name || e.attachments[0]?.filename
              return (
                <tr
                  key={e.id}
                  className="co-log-tree__row"
                  data-testid={`co-log-row-${e.id}`}
                  tabIndex={onOpenCo ? 0 : undefined}
                  role={onOpenCo ? 'button' : undefined}
                  onClick={onOpenCo ? () => onOpenCo(e.id) : undefined}
                  onKeyDown={onOpenCo ? (ev) => {
                    if (ev.key === 'Enter' || ev.key === ' ') {
                      ev.preventDefault()
                      onOpenCo(e.id)
                    }
                  } : undefined}
                >
                  <td>{coNumberLabel(e.co_number)}</td>
                  <td>
                    <span className="co-log-tree__title-cell">{e.title}</span>
                    {file ? (
                      <button
                        type="button"
                        className="co-log-tree__filelink"
                        data-testid={`co-log-file-${e.id}`}
                        onClick={(ev) => {
                          ev.stopPropagation()
                          onOpenCo?.(e.id)
                        }}
                      >
                        {file}
                      </button>
                    ) : null}
                  </td>
                  <td className={`co-log-tree__num${e.amount < 0 ? ' co-log-tree__amt--neg' : ''}`}>
                    {displaySignedAmount(e.amount, e.amount_display)}
                  </td>
                  <td className="co-log-tree__num">{formatUsd(e.running_contract)}</td>
                  <td>{coStatusBadge(e)}</td>
                </tr>
              )
            })}
            <tr className="co-log-tree__summary" data-testid="co-log-current">
              <td>—</td>
              <td>
                Current contract
                {pctLine ? <span className="co-log-tree__pct">{pctLine}</span> : null}
              </td>
              <td className="co-log-tree__num">
                {coDelta !== 0
                  ? (coDelta < 0 ? `−${formatUsd(Math.abs(coDelta))}` : `+${formatUsd(coDelta)}`)
                  : '—'}
              </td>
              <td className="co-log-tree__num">{formatUsd(tree.current_contract)}</td>
              <td>{coCount > 0 ? `${coCount} change order${coCount === 1 ? '' : 's'}` : '—'}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  )
}
